import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGlobalKey } from "@aviv-unified-frontend/internal";
import { useUserConsent } from "../../../utils/consentManagement";
import { useElapsed } from "../../../utils/useElapsed";
import { useCustomBannerTracking } from "./useCustomBannerTracking";
export const defaultBannerData = {
    dismiss: {
        count: 0,
        at: Date.now(),
    },
};
export const CUSTOM_BANNER_STORAGE_KEY = createGlobalKey("customBanner").toLowerCase();
export const getLocalBannerData = () => {
    const data = localStorage.getItem(CUSTOM_BANNER_STORAGE_KEY);
    try {
        return data ? JSON.parse(data) : defaultBannerData;
    }
    catch {
        return defaultBannerData;
    }
};
export const setLocalBannerData = (data) => {
    localStorage.setItem(CUSTOM_BANNER_STORAGE_KEY, JSON.stringify(data));
};
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HIDE_DURATION = {
    mobile: {
        1: DAY_IN_MS,
        2: 2 * DAY_IN_MS,
        3: 3 * DAY_IN_MS,
        4: 7 * DAY_IN_MS,
    },
    desktop: {
        1: 7 * DAY_IN_MS,
        2: 7 * DAY_IN_MS,
        3: 7 * DAY_IN_MS,
        4: 7 * DAY_IN_MS,
    },
};
export const getHideDuration = (count, isSmallViewport) => HIDE_DURATION[isSmallViewport ? "mobile" : "desktop"][count] ?? null;
export const shouldShowBanner = (data, isSmallViewport) => {
    const { at, count } = data.dismiss;
    if (count === 0)
        return true;
    const hideDuration = getHideDuration(count, isSmallViewport);
    if (!hideDuration)
        return false;
    return Date.now() - at >= hideDuration;
};
export const useCustomBanner = ({ isSmallViewport, }) => {
    const { onDisplayEvent, onClickEvent, onDismissEvent } = useCustomBannerTracking();
    const [canOpenModal, setCanOpenModal] = useState(false);
    const [bannerData, setBannerData] = useState(undefined);
    const userConsent = useUserConsent();
    const isLocalStorageUpdated = useRef(false);
    /**
     * Check if the elapsed time has passed before checking the banner visibility.
     * As the hide duration is different for mobile and desktop, this ensure that isSmallViewport is the correct value & not the fallback one.
     */
    const isElapsed = useElapsed({
        duration: 5,
        active: userConsent,
    });
    const shouldShow = useMemo(() => {
        return bannerData && isElapsed
            ? shouldShowBanner(bannerData, isSmallViewport)
            : false;
    }, [bannerData, isElapsed, isSmallViewport]);
    useEffect(() => {
        setBannerData(getLocalBannerData());
    }, []);
    useEffect(() => {
        setCanOpenModal(shouldShow);
    }, [shouldShow]);
    useEffect(() => {
        if (canOpenModal) {
            onDisplayEvent();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canOpenModal]);
    /***
     * Update the local banner data in local storage.
     * This function is called when the user interacts with the banner (e.g., clicks a link or dismisses the banner).
     *
     * Update is done only once to avoid incrementing the count multiple times.
     */
    const onUpdateBannerData = useCallback(() => {
        if (!bannerData || isLocalStorageUpdated.current)
            return;
        const nextData = {
            dismiss: {
                count: bannerData.dismiss.count + 1,
                at: Date.now(),
            },
        };
        setLocalBannerData(nextData);
        isLocalStorageUpdated.current = true;
    }, [bannerData]);
    const onClick = useCallback((link_text) => {
        onUpdateBannerData();
        onClickEvent(link_text);
    }, [onUpdateBannerData, onClickEvent]);
    const onDismiss = useCallback(() => {
        /**
         * Dismiss the banner and update local storage.
         */
        setCanOpenModal(false);
        onUpdateBannerData();
        onDismissEvent();
    }, [onUpdateBannerData, onDismissEvent]);
    return {
        canOpenModal,
        onDismiss,
        onClick,
    };
};
